import { FC, useRef, useState, useCallback, useMemo } from 'react'
import WavesurferPlayer from '@wavesurfer/react'
import { IconButton, Spinner, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { PiPause, PiPlay } from 'react-icons/pi'
import type WaveSurfer from 'wavesurfer.js'
import { theme } from '../tailwind.config'
import { formatTime } from '../utils/helpers/dates.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

interface WavesurferPlayerControlsProps {
  audioUrl: string
}

const WavesurferPlayerControls: FC<WavesurferPlayerControlsProps> = ({ audioUrl }) => {
  const { darkMode } = useDarkMode()
  const wavesurfer = useRef<WaveSurfer | null>(null)
  const [ready, setReady] = useState(false)
  const [duration, setDuration] = useState('00:00')
  const [progress, setProgress] = useState('00:00')
  const [isPlaying, setIsPlaying] = useState(false)
  const { colors: themeColors } = theme.extend

  const onReady = useCallback((ws: WaveSurfer) => {
    wavesurfer.current = ws
    const dur = ws.getDuration()
    const formattedDur = formatTime(dur)
    setDuration(formattedDur)
    setIsPlaying(false)
    setReady(true)
  }, [])

  const onPlayPause = useCallback(() => {
    if (!ready || !wavesurfer?.current) return
    wavesurfer.current?.playPause()
  }, [ready])

  const onTimeUpdate = useCallback((ws: WaveSurfer) => {
    const currTime = ws.getCurrentTime()
    const formattedCurrTime = formatTime(currTime)
    setProgress(formattedCurrTime)
  }, [])

  const playButtonColor = useMemo(() => {
    if (isPlaying) return 'accent'
    if (darkMode) return 'blue-gray'
    return 'primary'
  }, [isPlaying, darkMode])

  return (
    <div className="w-full" data-testid="agami-audio-waveform">
      <WavesurferPlayer
        height={50}
        waveColor={themeColors.primary[100]}
        cursorColor={darkMode ? 'white' : themeColors.primary[500]}
        progressColor={darkMode ? 'white' : themeColors.primary[500]}
        url={audioUrl}
        barGap={1}
        barWidth={3}
        barRadius={50}
        onReady={onReady}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeupdate={onTimeUpdate}
      />

      <div className="flex justify-between items-center mt-1">
        <div className="flex items-center gap-2">
          <IconButton
            size="sm"
            color={playButtonColor as colors}
            disabled={!ready}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onPlayPause()
            }}
          >
            {isPlaying ? <PiPause className="w-3 h-3" /> : <PiPlay className="w-3 h-3" />}
          </IconButton>

          {ready ? (
            <Typography className="font-barlow font-bold text-sm">{progress}</Typography>
          ) : (
            <div className="max-w-full animate-pulse">
              <Typography as="div" variant="h1" className="h-4 w-10 rounded bg-gray-300">
                &nbsp;
              </Typography>
            </div>
          )}
        </div>

        {!ready && <Spinner className="h-6 w-6 margin-auto self-center" />}

        {ready ? (
          <Typography className="font-barlow font-bold text-sm">{duration}</Typography>
        ) : (
          <div className="max-w-full animate-pulse">
            <Typography as="div" variant="h1" className="h-4 w-10 rounded bg-gray-300">
              &nbsp;
            </Typography>
          </div>
        )}
      </div>
    </div>
  )
}

export default WavesurferPlayerControls
